import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CiHeart } from "react-icons/ci";
import { FaInstagram } from "react-icons/fa";
import axios from "axios";
import Footer from "./Nav/Footer"; // Ensure this path is correct
import { ChevronLeft, ChevronRight } from "lucide-react";

const HomePage = () => {
  const [siteSetting, setSiteSetting] = useState({});
  const [error, setError] = useState(null);

  // Popular Items Data
  const popularItems = [
    { id: 1, name: "Coarse Tapioca Flour", price: 150, image: "/product_1.png" },
    { id: 2, name: "Glutinous Rice Flour", price: 123, image: "/product_2.png" },
    { id: 3, name: "Tapioca Starch", price: 126, image: "/product_3.png" },
    { id: 4, name: "Rice Flour", price: 350, image: "/product_4.png" },
    { id: 5, name: "Premium Rice Flour", price: 210, image: "/product_5.png" },
    { id: 6, name: "Special Tapioca Mix", price: 100, image: "/product_6.png" },
    { id: 7, name: "Golden Rice Flour", price: 600, image: "/product_7.png" },
    { id: 8, name: "ข้าวกวักเงินกวักทอง", price: 300, image: "/products/ข้าวกวักเงินกวักทอง01.png" },
    { id: 9, name: "ข้าวขาวตราพบแล้ว.png", price: 300, image: "/products/ข้าวขาวตราพบแล้ว.png" },
    { id: 10, name: "ข้ข้าวขาวตราบัวสุพรรณ", price: 300, image: "/products/ข้าวขาวตราบัวสุพรรณ.png" },
    { id: 11, name: "Fine Rice Powder", price: 300, image: "/product_8.png" },
  ];

  // New Arrivals Data
  const newArrivals = [
    { id: 12, name: "ข้าวขาวนาปีตราบัวพรกมล", price: 400, image: "/products/ข้าวขาวนาปีตราบัวพรกมล.png" },
    { id: 13, name: "ข้าวหอมมะลิขวัญใจหยก", price: 211, image: "/products/ข้าวหอมมะลิขวัญใจหยก01.png" },
    { id: 14, name: "ข้าวหอมมะลิดอกฝ้าย", price: 250, image: "/products/ข้าวหอมมะลิดอกฝ้าย01.png" },
    { id: 15, name: "ข้าวเหนียวเขี้ยวงู กข6", price: 234, image: "/products/ข้าวเหนียวเขี้ยวงู กข6.png" },
    { id: 16, name: "ข้าวเหนียวเขี้ยวงู ตราช้างต้นสน", price: 211, image: "/products/ข้าวเหนียวเขี้ยวงู ตราช้างต้นสน.png" },
    { id: 17, name: "ถุงแป้งข้าวหอมมะลิอินทรีย์", price: 250, image: "/products/ถุงแป้งข้าวหอมมะลิอินทรีย์.png" },
  ];

  const sizes = ["5KG", "10KG", "20KG", "25KG"];

  // Fetch Site Settings from API
  const getSiteDisplaySetting = () => {
    const token = localStorage.getItem("token");
    axios
      .get(`${process.env.REACT_APP_API}/api/v1/setting/site-display`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((res) => {
        const Setting = res.data.data;
        setSiteSetting(Setting);
      })
      .catch((err) => {
        console.error(err);
        setError("ไม่สามารถโหลดการตั้งค่าเว็บไซต์ได้ กรุณาลองใหม่อีกครั้ง.");
      });
  };

  useEffect(() => {
    getSiteDisplaySetting();
  }, []);

  // Carousel State and Handlers
  const [popularIndex, setPopularIndex] = useState(0);
  const [newArrivalsIndex, setNewArrivalsIndex] = useState(0);
  const [popularDirection, setPopularDirection] = useState(-1);
  const [newArrivalsDirection, setNewArrivalsDirection] = useState(1);
  const [itemsPerSlide, setItemsPerSlide] = useState(4);
  const [isMobile, setIsMobile] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  // Handle Resize for Responsive Design
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      if (window.innerWidth < 768) {
        setItemsPerSlide(1);
      } else if (window.innerWidth < 1024) {
        setItemsPerSlide(2);
      } else {
        setItemsPerSlide(4);
      }
    };

    handleResize(); // Initialize
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Convert itemsPerSlide to Tailwind Width Classes
  const getWidthClass = () => {
    switch (itemsPerSlide) {
      case 1:
        return "w-full";
      case 2:
        return "w-1/2";
      case 3:
        return "w-1/3";
      case 4:
      default:
        return "w-1/4";
    }
  };

  // Handle Pagination for Carousels
  const paginatePopular = (direction) => {
    setPopularDirection(direction);
    setPopularIndex((prev) => (prev + direction + popularItems.length) % popularItems.length);
  };

  const paginateNewArrivals = (direction) => {
    setNewArrivalsDirection(direction);
    setNewArrivalsIndex((prev) => (prev + direction + newArrivals.length) % newArrivals.length);
  };

  // Common Card Motion Variants (for smoother transitions)
  const cardVariants = {
    enter: (direction) => ({
      x: direction > 0 ? 50 : -50,
      opacity: 0,
    }),
    center: {
      x: 0,
      opacity: 1,
      transition: {
        ease: "easeInOut",
        duration: 0.6,
      },
    },
    exit: (direction) => ({
      x: direction < 0 ? 50 : -50,
      opacity: 0,
      transition: {
        ease: "easeInOut",
        duration: 0.4,
      },
    }),
  };

  // Simple fade-slide from left
  const slideInFromLeft = {
    hidden: { opacity: 0, x: -50 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.8, ease: "easeOut" },
    },
  };

  // Simple fade-slide from right
  const slideInFromRight = {
    hidden: { opacity: 0, x: 50 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.8, ease: "easeOut" },
    },
  };

  // For fade in
  const fadeInUp = {
    hidden: { opacity: 0, y: 40 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: "easeInOut" },
    },
  };

  // Get Current Slide Items
  const getSlideItems = (items, index) => {
    let slideItems = [];
    for (let i = 0; i < itemsPerSlide; i++) {
      slideItems.push(items[(index + i) % items.length]);
    }
    return slideItems;
  };

  // Format Price
  const formatPrice = (price) => {
    return `฿${Math.round(price)}`;
  };

  // Toggle Read More
  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  // ** Image Slider State and Handlers **
  const sliderImages = [
    "/products/1719053451665.jpg",
    "/products/1719053458992.jpg",
  ];

  const [currentSliderIndex, setCurrentSliderIndex] = useState(0);
  const [sliderDirection, setSliderDirection] = useState(1);

  const paginateSlider = (direction) => {
    setSliderDirection(direction);
    setCurrentSliderIndex((prev) => (prev + direction + sliderImages.length) % sliderImages.length);
  };

  // Auto-Slide for Image Slider
  useEffect(() => {
    const interval = setInterval(() => {
      paginateSlider(1);
    }, 5000); // Every 5 seconds
    return () => clearInterval(interval);
  }, []);

  // Image Slider Variants
  const sliderVariants = {
    enter: (direction) => ({
      x: direction > 0 ? 50 : -50,
      opacity: 0,
    }),
    center: {
      x: 0,
      opacity: 1,
      transition: {
        type: "tween",
        ease: "easeInOut",
        duration: 0.8,
      },
    },
    exit: (direction) => ({
      x: direction < 0 ? 50 : -50,
      opacity: 0,
      transition: {
        type: "tween",
        ease: "easeInOut",
        duration: 0.6,
      },
    }),
  };

  return (
    <div className="bg-blue-50 min-h-screen flex flex-col justify-between">
      <main className="container mx-auto mt-8 px-4">
        {/* Error Message */}
        {error && <div className="text-red-500 text-center mb-4">{error}</div>}

        {/* Hero Section */}
        <motion.section
          className="mb-12"
          variants={fadeInUp}
          initial="hidden"
          animate="visible"
        >
          <div className="relative h-[700px] rounded-lg overflow-hidden shadow-lg">
            <motion.img
              src="/BG_HERO.png"
              alt="Rice field"
              className="w-full h-full object-cover brightness-75"
              initial={{ scale: 1.1 }}
              animate={{ scale: 1 }}
              transition={{ duration: 2, ease: "easeOut" }}
              loading="lazy"
            />
            <motion.div
              className="absolute inset-0 bg-[#014487] bg-opacity-30 flex flex-col items-center justify-center p-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 0.5, ease: "easeInOut" }}
            >
              <motion.img
                className="w-3/4 object-contain md:w-1/4 md:h-1/4"
                src="/LOGO.png"
                alt="logo"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.5, ease: "easeOut" }}
                loading="lazy"
              />

              <motion.h1
                className="text-4xl font-semibold mt-4 text-center text-white"
                initial={{ y: -20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.8, ease: "easeOut" }}
              >
                {siteSetting.company_name
                  ? siteSetting.company_name
                  : "Pornkamon Rice Flour Mills Co.,Ltd."}
              </motion.h1>
            </motion.div>
          </div>
        </motion.section>

        {/* ** Full-Width Image Slider ** */}
        <motion.section
          className="mb-12"
          variants={fadeInUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <div className="relative w-full h-64 sm:h-80 md:h-96 lg:h-[500px] rounded-lg overflow-hidden shadow-lg">
            <AnimatePresence custom={sliderDirection}>
              <motion.img
                key={sliderImages[currentSliderIndex]}
                src={sliderImages[currentSliderIndex]}
                alt={`Slide ${currentSliderIndex + 1}`}
                className="absolute top-0 left-0 w-full h-full object-cover"
                custom={sliderDirection}
                variants={sliderVariants}
                initial="enter"
                animate="center"
                exit="exit"
                loading="lazy"
              />
            </AnimatePresence>

            {/* Navigation Buttons */}
            <button
              onClick={() => paginateSlider(-1)}
              className="absolute top-1/2 left-2 sm:left-4 transform -translate-y-1/2 bg-white bg-opacity-50 p-2 rounded-full hover:bg-opacity-75 transition-opacity"
              aria-label="Previous Slide"
            >
              <ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6 text-gray-800" />
            </button>
            <button
              onClick={() => paginateSlider(1)}
              className="absolute top-1/2 right-2 sm:right-4 transform -translate-y-1/2 bg-white bg-opacity-50 p-2 rounded-full hover:bg-opacity-75 transition-opacity"
              aria-label="Next Slide"
            >
              <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6 text-gray-800" />
            </button>
          </div>
        </motion.section>

        {/* Popular Items */}
        <motion.section
          className="mb-12"
          variants={fadeInUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <div className="flex flex-col sm:flex-row items-center justify-between mb-4">
            <motion.h2
              className="text-3xl font-bold text-blue-900 mb-4 sm:mb-0"
              variants={fadeInUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              Popular Items
            </motion.h2>
            <div className="flex gap-2">
              <button
                onClick={() => paginatePopular(1)} // Previous: direction=1 (left)
                className="p-2 rounded-full bg-[#014487] text-white hover:bg-blue-700 transition-colors"
                aria-label="Previous Popular Items"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              <button
                onClick={() => paginatePopular(-1)} // Next: direction=-1 (right)
                className="p-2 rounded-full bg-[#014487] text-white hover:bg-blue-700 transition-colors"
                aria-label="Next Popular Items"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            </div>
          </div>

          <div className="relative overflow-hidden">
            <div className="flex gap-6">
              {getSlideItems(popularItems, popularIndex).map((item, index) => (
                <AnimatePresence key={`${item.id}-${index}`} custom={popularDirection}>
                  <motion.div
                    className={`${getWidthClass()}`}
                    custom={popularDirection}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    variants={cardVariants}
                    transition={{
                      type: "tween",
                      ease: "easeInOut",
                      duration: 0.6,
                    }}
                  >
                    <div className="w-full relative rounded-lg p-4 flex flex-col items-center border border-gray-200 bg-white shadow-sm hover:shadow-md transition-shadow duration-300">
                      <img
                        src={item.image}
                        alt={item.name}
                        className="w-full h-48 object-contain mb-2 rounded-lg"
                        loading="lazy"
                      />
                      <div className="w-full max-h-36 text-center">
                        <h3 className="font-semibold text-xl text-blue-900">{item.name}</h3>
                        {/* <p className="font-bold text-lg text-gray-700">{formatPrice(item.price)}</p> */}
                      </div>

                      <div className="flex w-full justify-center space-x-2 mt-2">
                        {sizes.map((size, idx) => (
                          <div
                            key={idx}
                            className="flex justify-center items-center rounded-lg w-10 h-10 bg-white border-2 border-[#014487] text-[#014487] font-semibold transition-transform duration-200 hover:scale-110 hover:bg-blue-700 hover:text-white shadow cursor-pointer"
                          >
                            {size}
                          </div>
                        ))}
                      </div>

                      <div className="flex justify-between w-full mt-4 items-center">
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          className="bg-[#014487] text-white px-3 py-1 rounded font-medium"
                        >
                          อ่านเพิ่มเติม
                        </motion.button>
                        <div className="flex justify-center items-center w-10 h-10 rounded-full bg-[#014487] ml-auto cursor-pointer hover:scale-110 transition-transform">
                          <CiHeart className="text-white text-2xl" />
                        </div>
                      </div>
                    </div>
                  </motion.div>
                </AnimatePresence>
              ))}
            </div>
          </div>
        </motion.section>

        {/* New Arrivals */}
        <motion.section
          className="mb-12"
          variants={fadeInUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <div className="flex flex-col sm:flex-row items-center justify-between mb-4">
            <motion.h2
              className="text-3xl font-bold text-blue-900 mb-4 sm:mb-0"
              variants={fadeInUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              New Arrivals
            </motion.h2>
            <div className="flex gap-2">
              <button
                onClick={() => paginateNewArrivals(-1)} // Previous: direction=-1 (right)
                className="p-2 rounded-full bg-[#014487] text-white hover:bg-blue-700 transition-colors"
                aria-label="Previous New Arrivals"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              <button
                onClick={() => paginateNewArrivals(1)} // Next: direction=1 (left)
                className="p-2 rounded-full bg-[#014487] text-white hover:bg-blue-700 transition-colors"
                aria-label="Next New Arrivals"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            </div>
          </div>

          <div className="relative overflow-hidden">
            <div className="flex gap-6">
              {getSlideItems(newArrivals, newArrivalsIndex).map((item, index) => (
                <AnimatePresence key={`${item.id}-${index}`} custom={newArrivalsDirection}>
                  <motion.div
                    className={`${getWidthClass()}`}
                    custom={newArrivalsDirection}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    variants={cardVariants}
                    transition={{
                      type: "tween",
                      ease: "easeInOut",
                      duration: 0.6,
                    }}
                  >
                    <div className="relative rounded-lg p-4 flex flex-col items-center border bg-white shadow-sm hover:shadow-md transition-shadow">
                      <img
                        src={item.image}
                        alt={item.name}
                        className="w-full h-48 object-contain mb-2 rounded-lg"
                        loading="lazy"
                      />
                      <div className="w-full max-h-36 text-center">
                        <h3 className="font-semibold text-xl text-blue-900">{item.name}</h3>
                        {/* <p className="font-bold text-lg text-gray-700">{formatPrice(item.price)}</p> */}
                      </div>

                      <div className="flex w-full justify-center mt-2 space-x-2">
                        {sizes.map((size, idx) => (
                          <div
                            key={idx}
                            className="flex justify-center items-center rounded-lg w-10 h-10 bg-white border-2 border-[#014487] text-[#014487] font-semibold transition-transform duration-200 hover:scale-110 hover:bg-blue-700 hover:text-white shadow cursor-pointer"
                          >
                            {size}
                          </div>
                        ))}
                      </div>

                      <div className="flex justify-between w-full mt-4 items-center">
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          className="bg-[#014487] text-white px-3 py-1 rounded font-medium"
                        >
                          Add to Cart
                        </motion.button>
                        <div className="flex justify-center items-center w-10 h-10 rounded-full bg-[#014487] ml-auto cursor-pointer hover:scale-110 transition-transform">
                          <CiHeart className="text-white text-2xl" />
                        </div>
                      </div>
                    </div>
                  </motion.div>
                </AnimatePresence>
              ))}
            </div>
          </div>
        </motion.section>

        {/* Introduction Section with Slide-In Animation */}
        <motion.section
          className="mb-12 flex flex-col md:flex-row items-center"
          variants={fadeInUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <motion.div
            className="mx-auto w-full md:w-1/2 pr-4 mb-4 md:mb-0 flex flex-col justify-center"
            variants={fadeInUp}
          >
            <h2 className="text-3xl font-bold md:text-4xl text-blue-900 mb-2">
              ด้วยกรรมวิธีที่ได้มาตรฐานและ
            </h2>
            <h2 className="text-3xl font-bold mb-4 md:text-4xl text-blue-900">
              การคัดสรรวัตถุดิบที่มีคุณภาพ
            </h2>
            <AnimatePresence>
              <motion.p
                className="text-gray-700 w-full md:w-11/12 p-1 text-xl leading-relaxed"
                initial={{
                  height: isMobile && !isExpanded ? 0 : "auto",
                  opacity: isMobile && !isExpanded ? 0 : 1,
                }}
                animate={{
                  height: isMobile && !isExpanded ? "4.5em" : "auto",
                  opacity: isMobile && !isExpanded ? 0 : 1,
                }}
                transition={{ duration: 0.5, ease: "easeInOut" }}
              >
                บริษัทของเราได้ผลิตแป้งคุณภาพสูงมานานกว่า 40 ปี ด้วยความเชี่ยวชาญในกระบวนการผลิตที่ทันสมัยและการคัดสรรวัตถุดิบที่ดีที่สุด เพื่อให้มั่นใจว่าสินค้าของเราจะตอบสนองความต้องการของลูกค้าได้อย่างเต็มที่ ความมุ่งมั่นในการพัฒนาและนวัตกรรมทำให้เราสามารถรักษามาตรฐานสูงสุดในการผลิตและส่งออกแป้งไปยังหลายประเทศในภูมิภาคเอเชียตะวันออกเฉียงใต้ ด้วยความภาคภูมิใจในคุณภาพและบริการที่เป็นเลิศ เราพร้อมที่จะเป็นพันธมิตรที่คุณวางใจได้สำหรับทุกความต้องการด้านแป้งของคุณ
              </motion.p>
            </AnimatePresence>
            {/* "อ่านเพิ่มเติม" Button for Mobile */}
            {isMobile && (
              <motion.button
                onClick={toggleExpand}
                className="mt-2 text-[#014487] flex items-center focus:outline-none"
                whileHover={{ scale: 1.05 }}
              >
                {isExpanded ? "อ่านน้อยลง" : "อ่านเพิ่มเติม"}
                <motion.svg
                  xmlns="http://www.w3.org/2000/svg"
                  className={`h-6 w-6 ml-1 transition-transform duration-300 ${
                    isExpanded ? "rotate-180" : "rotate-0"
                  }`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </motion.svg>
              </motion.button>
            )}
          </motion.div>
          <motion.div
            className="flex justify-center items-center w-full md:w-1/2"
            variants={slideInFromLeft}
          >
            <motion.img
              src="/Innovation.jpeg"
              alt="Innovation in Rice Processing"
              className="w-full h-auto max-h-[323px] object-cover rounded-lg shadow-md"
              whileHover={{ scale: 1.02 }}
              loading="lazy"
            />
          </motion.div>
        </motion.section>

        {/* Production Process */}
        <motion.section
          className="flex flex-col overflow-hidden mb-12 px-4"
          variants={fadeInUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {/* Section Title */}
          <motion.h2
            className="text-4xl font-extrabold mb-4 text-center text-blue-900"
            variants={fadeInUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            ขั้นตอนการผลิต
          </motion.h2>

          {/* SEO-Friendly Description */}
          <motion.p
            className="text-gray-700 text-center mb-8 max-w-2xl mx-auto text-lg leading-relaxed"
            variants={fadeInUp}
          >
            ผ่านกรรมวิธีการผลิตที่ได้มาตรฐานและการคัดสรรวัตถุดิบที่มีคุณภาพสูงสุด เพื่อให้ได้แป้งข้าวที่ดีที่สุดสำหรับลูกค้าทุกท่านจากหลายทวีปทั่วโลกด้วยความใส่ใจและมาตรฐานสากล
          </motion.p>

          {/* Production Steps */}
          <motion.div
            className="w-full flex flex-col md:flex-row justify-center items-center space-y-8 md:space-y-0 md:space-x-8"
            variants={{
              visible: {
                transition: {
                  staggerChildren: 0.3,
                },
              },
            }}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {/* Step 1 */}
            <motion.div
              className="relative flex flex-col items-center bg-white rounded-lg shadow-lg p-6 w-full max-w-sm"
              variants={fadeInUp}
            >
              {/* Animated Gradient Border */}
              <div className="absolute inset-0 rounded-lg border-[3px] border-transparent bg-gradient-to-r from-[#014487] via-[#4fa3ff] to-[#014487] animate-gradient-rotate bg-clip-border"></div>

              {/* Inner Content */}
              <div className="relative z-10 bg-white rounded-lg w-full h-full flex flex-col items-center p-4">
                <motion.img
                  src="https://www.afpm.cn/assets/images/slides/slide1.jpg?1733529600045"
                  alt="ขั้นตอนการผลิตที่ผ่านการคัดสรรวัตถุดิบคุณภาพ"
                  className="w-full h-48 object-cover rounded-lg mb-4"
                  whileHover={{ scale: 1.05 }}
                  transition={{ duration: 0.3, ease: "easeInOut" }}
                  loading="lazy"
                />
                <h3 className="text-xl font-semibold text-blue-900 mb-2">
                  การคัดสรรวัตถุดิบ
                </h3>
                <p className="text-gray-600 text-center">
                  เราเลือกใช้ข้าวคุณภาพสูงที่ผ่านการตรวจสอบอย่างเข้มงวด เพื่อให้มั่นใจในความบริสุทธิ์และคุณค่าทางโภชนาการ
                </p>
              </div>
            </motion.div>

            {/* Step 2 */}
            <motion.div
              className="relative flex flex-col items-center bg-white rounded-lg shadow-lg p-6 w-full max-w-sm"
              variants={fadeInUp}
            >
              {/* Animated Gradient Border */}
              <div className="absolute inset-0 rounded-lg border-[3px] animate-gradient-border border-transparent bg-gradient-to-r from-[#014487] via-[#4fa3ff] to-[#014487] bg-clip-border"></div>

              {/* Inner Content */}
              <div className="relative z-10 bg-white rounded-lg w-full h-full flex flex-col items-center p-4">
                <motion.img
                  src="https://thumbs.dreamstime.com/b/scientist-taking-wheat-pincette-lab-research-food-close-up-106893235.jpg"
                  alt="ขั้นตอนการผลิตที่ผ่านการตรวจสอบคุณภาพ"
                  className="w-full h-48 object-cover rounded-lg mb-4"
                  whileHover={{ scale: 1.05 }}
                  transition={{ duration: 0.3, ease: "easeInOut" }}
                  loading="lazy"
                />
                <h3 className="text-xl font-semibold text-blue-900 mb-2">
                  การผลิตที่ทันสมัย
                </h3>
                <p className="text-gray-600 text-center">
                  ด้วยเทคโนโลยีการผลิตที่ทันสมัย เราสามารถผลิตแป้งข้าวที่มีความละเอียดและคุณภาพสูงสุดได้อย่างต่อเนื่อง
                </p>
              </div>
            </motion.div>

            {/* Step 3 */}
            <motion.div
              className="relative flex flex-col items-center bg-white rounded-lg shadow-lg p-6 w-full max-w-sm"
              variants={fadeInUp}
            >
              {/* Animated Gradient Border */}
              <div className="absolute inset-0 rounded-lg border-[3px] animate-gradient-border border-transparent bg-gradient-to-r from-[#014487] via-[#4fa3ff] to-[#014487] bg-clip-border"></div>

              {/* Inner Content */}
              <div className="relative z-10 bg-white rounded-lg w-full h-full flex flex-col items-center p-4">
                <motion.img
                  src="https://blog.indumak.com.br/wp-content/uploads/2022/12/how-to-make-the-rice-packing-process-later-1-2.jpg"
                  alt="ขั้นตอนการบรรจุภัณฑ์ที่มีประสิทธิภาพ"
                  className="w-full h-48 object-cover rounded-lg mb-4"
                  whileHover={{ scale: 1.05 }}
                  transition={{ duration: 0.3, ease: "easeInOut" }}
                  loading="lazy"
                />
                <h3 className="text-xl font-semibold text-blue-900 mb-2">
                  การบรรจุภัณฑ์
                </h3>
                <p className="text-gray-600 text-center">
                  กระบวนการบรรจุภัณฑ์ที่มีประสิทธิภาพช่วยให้แป้งข้าวของเราคงความสดใหม่และปลอดภัยในการใช้งาน
                </p>
              </div>
            </motion.div>
          </motion.div>
        </motion.section>

        {/* Factory / Variety Section with Slide-In Animation */}
        <motion.section
          className="mb-12 flex flex-col md:flex-row mt-4 items-center"
          variants={fadeInUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <motion.div
            className="flex justify-center items-center w-full md:w-1/2 mb-4 md:mb-0"
            variants={slideInFromRight}
          >
            <motion.img
              src="/Factory.jpeg"
              alt="Factory Overview"
              className="w-full h-auto max-h-[323px] object-cover md:rounded-lg shadow-md md:w-10/12"
              whileHover={{ scale: 1.02 }}
              loading="lazy"
            />
          </motion.div>
          <motion.div
            className="w-full md:w-1/2 pr-4 flex flex-col mt-2.5 justify-center items-start"
            variants={fadeInUp}
          >
            <h2 className="text-3xl font-extrabold mb-2.5 md:text-4xl text-blue-900">
              Pornkamon Offers a Wide
            </h2>
            <h2 className="text-3xl font-extrabold mb-4 md:text-4xl text-blue-900">
              Variety of Rice Flours
            </h2>
            <p className="text-gray-700 w-full p-1 leading-relaxed">
              Pornkamon Offers a Wide Variety of Rice Flours is Certified for:
              GMP, HACCP, HALAL, KOSHER, US FDA, Organic EU, NOP, COR. ด้วยความมุ่งมั่นในการผลิตแป้งที่มีคุณภาพสูงสุด เราจึงได้รับการรับรองมาตรฐานต่างๆ เพื่อให้มั่นใจว่าสินค้าของเราสามารถตอบสนองความต้องการของลูกค้าได้อย่างครบถ้วนและมีความน่าเชื่อถือสูง
            </p>
          </motion.div>
        </motion.section>

        <motion.section
          className="mb-12 flex flex-col md:flex-row mt-4 items-center"
          variants={fadeInUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {/* ข้อความอยู่ทางซ้าย */}
          <motion.div
            className="w-full md:w-1/2 pr-4 flex flex-col mt-2.5 justify-center items-start"
            variants={fadeInUp}
          >
            <h2 className="text-3xl font-extrabold mb-2.5 md:text-4xl text-blue-900">
              Supporting Local Communities
            </h2>
            <p className="text-gray-700 w-full p-1 leading-relaxed">
              เราให้ความสำคัญกับการสนับสนุนชุมชนท้องถิ่นและเกษตรกรอย่างจริงใจ ด้วยความมุ่งมั่นที่จะสร้างความเปลี่ยนแปลงในทางที่ดีแก่ทุกภาคส่วนในสังคม การดำเนินงานของเรามุ่งเน้นการให้บริการที่ได้มาตรฐานสูงและตอบสนองความต้องการของเกษตรกรอย่างมีประสิทธิภาพ พร้อมทั้งนำเสนอโซลูชันที่ยั่งยืน เพื่อส่งเสริมการพัฒนาเศรษฐกิจท้องถิ่นอย่างมั่นคงและสมดุล นอกจากนี้ เรายังยึดมั่นในแนวทางการพัฒนาที่เป็นมิตรต่อสิ่งแวดล้อม โดยผสานความเจริญทางเศรษฐกิจเข้ากับการดูแลทรัพยากรธรรมชาติอย่างยั่งยืน เพื่อสร้างสังคมที่ดีขึ้นและอนาคตที่มั่นคงสำหรับคนรุ่นต่อไป
            </p>
          </motion.div>

          {/* รูปภาพอยู่ทางขวา */}
          <motion.div
            className="flex justify-center items-center w-full md:w-1/2 mb-4 md:mb-0"
            variants={slideInFromRight}
          >
            <motion.img
              src="https://t3.ftcdn.net/jpg/09/48/77/22/360_F_948772208_YUr0O42T7GqzgfGnPwZdsmrjGcOU8DMN.jpg"
              alt="Factory Overview"
              className="w-full h-auto max-h-[323px] object-cover md:rounded-lg shadow-md md:w-10/12"
              whileHover={{ scale: 1.02 }}
              loading="lazy"
            />
          </motion.div>
        </motion.section>

        {/* Quality Certificates */}
        <motion.section
          className="flex flex-col overflow-hidden mt-4 mb-16"
          variants={fadeInUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <motion.h2
            className="text-4xl font-extrabold mb-4 text-center text-blue-900"
            variants={fadeInUp}
          >
            การรับรองคุณภาพระดับสากล
          </motion.h2>

          <div className="w-full h-full flex flex-col justify-around items-center md:flex-row flex-wrap gap-4">
            {[1, 2, 3, 4, 5].map((num) => (
              <motion.div
                key={num}
                variants={fadeInUp}
                transition={{ delay: num * 0.1 }}
              >
                <motion.img
                  src={`/cert_${num}.jpg`}
                  alt={`Certificate ${num}`}
                  className="w-44 h-auto object-contain rounded-lg shadow hover:shadow-lg transition-shadow"
                  whileHover={{ scale: 1.05 }}
                  loading="lazy"
                />
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* Industry 4.0 Section */}
        <motion.h2
          className="text-4xl font-extrabold mb-4 text-center text-blue-900"
          variants={fadeInUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          transition={{
            type: "spring",
            stiffness: 100,
            damping: 15,
            duration: 0.8,
          }}
        >
          เรามุ่งพัฒนาสู่อุตสาหกรรม 4.0
        </motion.h2>
        <motion.p
          className="text-gray-700 text-center mb-8 max-w-2xl mx-auto text-lg leading-relaxed"
          variants={fadeInUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          transition={{
            duration: 1,
            delay: 0.5,
            ease: "easeInOut",
          }}
        >
          โรงงานแป้งพรกมลได้พัฒนาระบบ ERP ขึ้นมาเพื่อควบคุมและจัดการขั้นตอนต่างๆ ภายในโรงงาน ตั้งแต่การนำเข้าวัตถุดิบ การตรวจสอบคุณภาพ การผลิต จนถึงการส่งออก นอกจากนี้ เรายังนำเทคโนโลยีปัญญาประดิษฐ์ (AI) มาใช้ในการตรวจสอบคุณภาพสินค้า (QC) ที่ผลิตขึ้นมา เพื่อให้มั่นใจในมาตรฐานและประสิทธิภาพสูงสุด
        </motion.p>

        {/* AI / ERP Section */}
        <motion.section
          className="mb-12 flex flex-col md:flex-row mt-4 items-center"
          variants={fadeInUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <motion.div
            className="flex justify-center items-center w-full md:w-1/2 mb-4 md:mb-0"
            variants={slideInFromRight}
          >
            <motion.img
              src="/BGAI.png"
              alt="Factory Overview"
              className="w-full h-auto max-h-[323px] object-cover md:rounded-lg shadow-md md:w-10/12"
              whileHover={{ scale: 1.02 }}
              loading="lazy"
            />
          </motion.div>
          <motion.div
            className="w-full md:w-1/2 pr-4 flex flex-col mt-2.5 justify-center items-start"
            variants={fadeInUp}
          >
            <h2 className="text-3xl font-extrabold mb-2.5 md:text-4xl text-blue-900">
              Advanced Technology in Our Flour Factory: AI Integration & Custom ERP System
            </h2>
            <p className="text-gray-700 w-full p-1 leading-relaxed">
              At Pornkamon, we leverage cutting-edge Artificial Intelligence and a proprietary ERP system to optimize our flour production processes. Our innovative technologies ensure superior quality, enhanced efficiency, and seamless operations, setting us apart in the flour manufacturing industry.
            </p>
          </motion.div>
        </motion.section>

        {/* Quotation Section */}
        <motion.div
          className="text-center flex flex-col items-center justify-center my-8"
          variants={fadeInUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {/* Top Quotation Mark */}
          <motion.div
            className="text-5xl text-blue-900 font-bold mb-4"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2, ease: "easeOut" }}
          >
            “
          </motion.div>

          {/* Main Text */}
          <motion.h2
            className="text-xl md:text-4xl font-bold text-blue-900 leading-relaxed max-w-2xl"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.5, ease: "easeInOut" }}
          >
            คิดถึงแป้ง คิดถึงข้าว คิดถึงเรา ไว้ใจเรา <br />
            เพื่อทุกมื้ออาหารที่เต็มเปี่ยมด้วยคุณภาพและความรัก <br />
            เลือกผลิตภัณฑ์จากเราเพื่อสุขภาพที่ดีของคุณและครอบครัวพร้อมบริการที่คุณไว้วางใจได้เสมอ
          </motion.h2>

          {/* Bottom Quotation Mark */}
          <motion.div
            className="text-5xl text-blue-900 font-bold mt-4"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.8, ease: "easeOut" }}
          >
            ”
          </motion.div>
        </motion.div>
      </main>

      <Footer />
    </div>
  );
};

export default HomePage;
